import React, { useEffect, useState } from "react";
import Dialog from "rc-dialog";
import DatePicker from "react-datepicker";
import moment from "moment";
import axios from "axios";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReactTooltip from "react-tooltip";

import { EndTermToggle } from "../../../components/EndTermToggle";
import { formalName } from "../../../utils/names";
import { isObjectLiteral, isString } from "../../../utils/types";
import { EmailRequiredToolTip, MissingEmailTooltip } from "./tooltips";
import { hasAnyRole, hasPermission, useCurrentUser } from "../../../utils/auth";

/**
 * Find student dialog which is able to add students to the given course.
 * @param {object} course Course data
 * @param {Array<object>} events Events data
 * @param {Function} addGlobalMessage
 * @param props
 * @returns {JSX.Element}
 */
export default function FindStudent({ course, events, addGlobalMessage, ...props }) {
    const [dialog, setDialog] = useState(null); // "createStudent"
    const [userToAdd, setUserToAdd] = useState(null);
    const [createStudentDefaultValues, setCreateStudentDefaultValues] = useState(null);
    const [competenceProfile, setCompetenceProfile] = useState(null);
    const [endTerms, setEndTerms] = useState([]);

    const courseContainsELearning = !!(events && events.find((event) => event.type === "elearning"));
    const currentStudentUUIDS = course.students.map((student) => student.uuid);

    /**
     * Selects the given user to be added to the course. This shows <AddStudentDialog />
     * to possibly update the user and then add it to the course.
     * @param {object} user
     * @param {boolean} [isImported] Whether the user was imported from ProCRM.
     */
    const selectUserToAdd = (user, isImported = false) => {
        setUserToAdd(Object.assign(user, { isImported: !!isImported }));
        setDialog("addStudent");
    };

    /**
     * Reloads the data of the `userToAdd`.
     * @returns {Promise}
     */
    const refreshUserToAdd = () => {
        if (!userToAdd) {
            console.warn("No user to refresh");
            return Promise.reject();
        }
        return axios.get(`/v1/users/${userToAdd.uuid}/`).then(
            (response) => {
                setUserToAdd(response.data);
                return response.data;
            },
            (error) => Promise.reject(error)
        );
    };

    /**
     * Uses the given user data to add a student to this course.
     * @param user
     * @param {string[]} endTermsToExempt list of end term UUIDs to exempt.
     */
    const addUserToCourse = async (user, endTermsToExempt) => {
        // Start by adding the user to the course:
        let addedUser;
        try {
            const response = await axios.post(`/v1/courses/${course.uuid}/students/`, {
                uuid: user.uuid
            });

            if (Array.isArray(endTermsToExempt)) {
                const exemptionsPayload = endTermsToExempt.map((e) => ({
                    user: user.uuid,
                    course: course.uuid,
                    end_term: e,
                    competenceProfile: course.competence_profile
                }));

                await axios.post(`/v1/courses/${course.uuid}/exemptions/`, exemptionsPayload);
            }

            addedUser = Array.isArray(response.data) ? response.data[0] : response.data;
            addGlobalMessage({
                title: `De student '${addedUser.full_name}' is succesvol toegevoegd aan deze cursus.`,
                link: `/courses/edit/${course.uuid}/students`
            });
        } catch (error) {
            addGlobalMessage({
                title: `Er is een fout opgetreden bij het toevoegen van de student '${user.full_name}' aan deze cursus.`,
                type: "warning"
            });
            return Promise.reject(error.response?.data);
        }

        // user is added, tell our parent to update the data:
        props.getData();
        window.location.reload();

        // if that succeeds we can set student options:
        const studentOptions = {
            insured: user.insured,
            employer_specific: user.employer_specific
        };
        if (course.insurance_available && user.insured) {
            studentOptions["insured"] = user.insured;
        }
        if (course.employer_specific_available && user.employer_specific) {
            studentOptions["employer_specific"] = user.employer_specific;
        }
        if (Object.keys(studentOptions).length > 0) {
            try {
                await axios.patch(`/v1/courses/${course.uuid}/`, {
                    student_options: [
                        {
                            user: user.uuid,
                            ...studentOptions
                        }
                    ]
                });
            } catch (error) {
                addGlobalMessage({
                    title: `Er is een fout opgetreden bij het opgeven van de opties van de student '${user.full_name}' op deze cursus.`,
                    type: "warning"
                });
                console.warn("Error while updating student options.", error);
                return Promise.reject(error.response.data);
            }
        }

        // all went well, close the dialog:
        setDialog(null);
        setUserToAdd(null);

        // user is changed, tell our parent to update the data as it might be changed
        props.getData();
        window.location.reload();
        return Promise.resolve(addedUser);
    };

    useEffect(async () => {
        const res = await axios.get(`/v1/competenceprofiles/${course.competence_profile}/end_terms/`);
        const resCompetenceProfile = await axios.get(`/v1/competenceprofiles/${course.competence_profile}/`);

        setCompetenceProfile(resCompetenceProfile.data);
        setEndTerms(res.data);
    }, [course?.competence_profile]);

    return (
        <React.Fragment>
            <button type="button" disabled={course.state === "finished"} className="btn btn-primary" onClick={() => setDialog("findStudent")}>
                Cursist toevoegen
                <i className="fa fa-plus" />
            </button>

            <FindStudentDialog
                visible={dialog === "findStudent"}
                containsELearning={courseContainsELearning}
                currentStudentUUIDS={currentStudentUUIDS}
                onSelect={selectUserToAdd}
                onCreate={(defaultValues) => {
                    defaultValues && setCreateStudentDefaultValues(defaultValues);
                    setDialog("createStudent");
                }}
                onClose={() => setDialog(null)}
            />

            <AddStudentDialog
                endTerms={endTerms}
                competenceProfile={competenceProfile}
                course={course}
                visible={dialog === "addStudent"}
                student={userToAdd}
                imported={userToAdd?.isImported}
                containsELearning={courseContainsELearning}
                insuranceAvailable={course?.insurance_available}
                employerSpecificAvailable={course?.employer_specific_available}
                onSubmit={addUserToCourse}
                onRefresh={refreshUserToAdd}
                onCancel={() => setDialog("findStudent")}
                onClose={() => setDialog(null)}
            />

            <CreateStudentDialog
                endTerms={endTerms}
                competenceProfile={competenceProfile}
                course={course}
                visible={dialog === "createStudent"}
                containsELearning={courseContainsELearning}
                insuranceAvailable={course?.insurance_available}
                employerSpecificAvailable={course?.employer_specific_available}
                onSubmit={addUserToCourse}
                defaultValues={createStudentDefaultValues}
                onCancel={() => {
                    setDialog("findStudent");
                    setCreateStudentDefaultValues(null);
                }}
                onClose={() => {
                    setDialog(null);
                    setCreateStudentDefaultValues(null);
                }}
            />
        </React.Fragment>
    );
}

/**
 * Wraps <DatePicker />
 * @param name
 * @param value
 * @param onChange
 * @param readOnly
 * @param props
 * @returns {JSX.Element}
 */
function DateOfBirthPicker({ name, value, onChange, readOnly, ...props }) {
    return (
        <DatePicker
            name={name}
            onChange={(date) => onChange(date ? date.toISOString() : null)}
            // onBlur={handleSubmit}
            onChangeRaw={(event) => {
                let value = event.target.value.split("-").join("");
                let newDate = value.substring(0, 2);
                if (value.length >= 3) {
                    newDate += "-" + value.substring(2, 4);
                }
                if (value.length >= 5) {
                    let checkYear = value.substring(4, 8);
                    if (checkYear.length === 2 && checkYear !== "19" && checkYear !== "20") {
                        newDate += "-19" + value.substring(4, 8);
                    } else {
                        newDate += "-" + value.substring(4, 8);
                    }
                }
                event.target.value = newDate;
            }}
            className="form-control"
            placeholderText="Geboortedatum"
            selected={value && moment(value)}
            maxDate={moment()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            autoComplete="off"
            readOnly={readOnly}
            {...props}
        />
    );
}

function defaultPrevented(callback, ...args) {
    return (event) => {
        event.preventDefault();
        callback && callback(...args);
    };
}

/**
 * Generic <input> component with a <label> and errors.
 * todo: Lift up to global reusable form components.
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{readonly errors?: *, readonly label?: *, readonly required?: *, readonly name?: *, readonly className?: *}> & React.RefAttributes<unknown>>}
 */
const Input = React.forwardRef(({ name, label, required, errors, className = "form-control", ...props }, ref) => {
    const id = `field-${name}`;
    return (
        <>
            {label && (
                <label htmlFor={id}>
                    {label}
                    {required && " *"}
                </label>
            )}
            <input id={id} name={name} className={className} required={required} ref={ref} {...props} />
            {errors && <FormError error={errors[name]} />}
        </>
    );
});

/**
 * Generic <select> component with <label> and errors.
 * todo: Lift up to global reusable form components.
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{readonly errors?: *, readonly label?: *, readonly required?: *, readonly name?: *, readonly className?: *}> & React.RefAttributes<unknown>>}
 */
const Select = React.forwardRef(({ name, label, required, errors, className = "form-control", ...props }, ref) => {
    const id = `field-${name}`;
    return (
        <>
            {label && (
                <label htmlFor={id}>
                    {label}
                    {required && " *"}
                </label>
            )}
            <select id={id} name={name} className={className} required={required} ref={ref} {...props} />
            {errors && <FormError error={errors[name]} />}
        </>
    );
});

/**
 * Generic form error component.
 * todo: Lift up to global reusable form components.
 * @param {{message: string, type: string}} error
 * @param {string} [fieldName]
 * @returns {JSX.Element|null}
 */
function FormError({ error, fieldName }) {
    return error && error.message ? (
        <div className="form-error alert alert-danger">
            {fieldName && <strong>{fieldName} </strong>}
            {error.message}
        </div>
    ) : null;
}

/**
 * Help text for the E-mail field which reacts to the value and displays a tooltip
 * to explain the importance of the E-mail address.
 * @param {String} value The current value of the E-mail field. Use watch() to get the
 * current value.
 * @param {Boolean} containsELearning Whether the course contains e-learning.
 * @returns {JSX.Element}
 */
function EmailHelpText({ value, containsELearning = false }) {
    return !value ? (
        <>
            <span className="text-muted">Let op, er is geen E-mailadres opgegeven.</span>
            {containsELearning ? <EmailRequiredToolTip ELearning={true} /> : <MissingEmailTooltip />}
        </>
    ) : (
        <>&nbsp;</>
    );
}

const findStudentFormSchema = yup
    .object({
        // Matches both the old and new NIBHV number:
        nibhv_number: yup
            .string()
            .label("NIBHV nummer")
            .matches(/^\d{3}-?\d{3}-?\d{3}$/, {
                message: "Dit is geen geldig NIBHV nummer.",
                excludeEmptyString: true
            }),
        // nibhv_number: yup.string().matches(/\d{3}-?\d{3}-?\d{3}/).when(["last_name", "date_of_birth"], {
        //     // when last_name AND date_of_birth
        //     is: (last_name, date_of_birth) => last_name && date_of_birth,
        //     // then there's no length requirement:
        //     then: yup.string().min(0),
        //     // else, it's required and should be 9 to 11 characters long:
        //     otherwise: yup.string().required("Geef een NIBHV nummer of of achternaam + geboortedatum.").min(9).max(11),
        // }),
        last_name: yup
            .string()
            .label("Achternaam")
            .when("nibhv_number", {
                // when the nibhv_number is set:
                is: (nibhv_number) => nibhv_number.match(/\d{3}-?\d{3}-?\d{3}/),
                // then not required
                then: yup.string().min(0),
                // else it's required:
                otherwise: yup.string().required("Zonder een NIBHV nummer is dit veld verplicht.")
            }),
        date_of_birth: yup
            .string()
            .label("Geboortedatum")
            .nullable()
            .when("nibhv_number", {
                // when the nibhv_number is set:
                is: (nibhv_number) => !!nibhv_number,
                // then not required
                then: yup.string().nullable().min(0),
                // else it's required:
                otherwise: yup.string().nullable().required("Zonder een NIBHV nummer is dit veld verplicht.")
            })
    })
    .required();

/**
 * Dialog to find an existing user.
 * @param {boolean} visible Whether the <Dialog /> is visible.
 * @param {boolean} containsELearning Whether the course contains an e-learning event.
 * @param {Array<string>} currentStudentUUIDS UUID's of the current students
 * @param {Function} onSelect Callback to select the user to add to the course.
 * @param {Function} onCreate Callback to create a new user.
 * @param {Function} onClose Callback to close the dialog.
 * @returns {JSX.Element}
 */
function FindStudentDialog({ visible, containsELearning, currentStudentUUIDS, onSelect, onCreate, onClose }) {
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(findStudentFormSchema)
    });
    const [loading, setLoading] = useState(0);
    const [previousCourses, setPreviousCourses] = useState([]);
    const [results, setResults] = useState(null);
    const [proCRMResults, setProCRMResults] = useState(null);

    const resetDialog = () => {
        reset({
            nibhv_number: "",
            last_name: "",
            date_of_birth: null
        });
        setResults(null);
        setProCRMResults(null);
    };

    const handleClose = defaultPrevented(() => {
        resetDialog();
        onClose && onClose();
    });

    /**
     * Executes a search with the form data.
     * @param {string} nibhv_number
     * @param {string} last_name
     * @param {string} date_of_birth
     */
    const onSubmit = ({ nibhv_number, last_name, date_of_birth }) => {
        if (!(nibhv_number || (last_name && date_of_birth))) {
            return;
        }
        const formattedDOB = date_of_birth ? moment(date_of_birth).format("YYYY-MM-DD") : null;

        // search for our own users:
        const params = {
            pagination: "false",
            is_active: "true",
            fields: [
                "uuid",
                "nibhv_number",
                "full_name",
                "first_name",
                "last_name_prefix",
                "last_name",
                "email",
                "date_of_birth",
                "eol_id", // todo: we should replace eol_id with something else that says "managed externally".
                "read_only_fields"
            ].join(",")
        };
        if (nibhv_number) {
            Object.assign(params, { nibhv_number });
        } else {
            Object.assign(params, {
                last_name,
                date_of_birth: formattedDOB
            });
        }
        axios
            .get(`/v1/users/?${new URLSearchParams(params)}`, {
                // cancelToken
            })
            .then((response) => {
                setResults(response.data);

                if (response.data.length > 0)
                    axios
                        .get(`/v1/users/${response.data[0].uuid}/courses/?will_attend=true&pre_checks=true&limit=10`)
                        .then((res) => setPreviousCourses(res.data.results));
                else setPreviousCourses([]);
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return;
                }
                if (error.response && error.response.data) {
                    console.warn("Errors", error.response.data);
                }
            })
            .finally(() => setLoading((loaders) => loaders - 1));
        setLoading((loaders) => loaders + 1);

        const proCRMParams = {};
        if (nibhv_number) {
            // are we searching with an "old" nibhv number?
            Object.assign(proCRMParams, {
                nibhvnr: nibhv_number
            });
        } else {
            Object.assign(proCRMParams, {
                last_name,
                date_of_birth: formattedDOB
            });
        }
        axios
            .get(`/v1/procrm/?${new URLSearchParams(proCRMParams)}`, {
                // cancelToken
            })
            .then((response) => setProCRMResults(response.data))
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return;
                }
                if (error.response && error.response.data) {
                    console.warn("ProCRM errors", error.response.data);
                }
            })
            .finally(() => setLoading((loaders) => loaders - 1));
        setLoading((loaders) => loaders + 1);
    };

    /**
     * Passes the selected user data to `onSelect` and triggers closing the dialog.
     * @param {object} data
     * @param {boolean} [isImported=false]
     */
    const handleSelect = (data, isImported = false) => {
        onSelect(data, isImported);
        resetDialog();
        // onClose();
    };

    /**
     * Imports a user from ProCRM using the given ProCRM relation ID.
     * @param {number} pro_crm_relation_id
     */
    const handleImportAndSelect = (pro_crm_relation_id) => {
        // first import the student:
        axios
            .post("/v1/procrm/", {
                pro_crm_relation_id
            })
            .then((response) => {
                const user = response.data;
                // then select the user:
                handleSelect(user, true);
            })
            .catch((error) => {
                // todo: inform the user that the import failed!!
                if (axios.isCancel(error)) {
                    return;
                }
                if (error.response && error.response.data) {
                    console.warn("Error while importing student!", error.response.data);
                }
            });
    };

    /**
     * User chose to create a new user. We call the `onCreate` callback and pass it our
     * form values so it can pre-fill form fields.
     */
    const handleCreate = defaultPrevented(() => {
        const formValue = getValues();
        onCreate && onCreate(formValue);
        resetDialog();
    });

    register("date_of_birth");
    // extract the current value
    const dateOfBirth = watch("date_of_birth");

    const labels = {
        nibhv_number: "NIBHV-nummer",
        last_name: "Achternaam",
        date_of_birth: "Geboortedatum"
    };

    return (
        <Dialog title="Cursist toevoegen" onClose={handleClose} visible={visible}>
            {containsELearning && (
                <div className="alert alert-info">
                    Deze cursus bevat e-learning. E-learning inschrijvingen worden elke dinsdagochtend gefactureerd. Zorg ervoor dat maandag alle inschrijvingen correct
                    zijn ingevuld (vrijstellingen, aanwezigheid etc.). De cursist ontvangt automatisch een uitnodiging vier weken voor aanvang van de cursus.
                </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-row align-items-center mt-3 mb-3">
                    <div className="col-2">
                        <div className="input-group">
                            <Input {...register("nibhv_number")} maxLength={11} type="text" placeholder="NIBHV#" autoFocus />
                        </div>
                    </div>
                    <div className="col-auto">of</div>
                    <div className="col-3">
                        <div className="input-group">
                            <Input {...register("last_name")} type="text" placeholder={labels.last_name} />
                        </div>
                    </div>
                    <div className="col-auto">en</div>
                    <div className="col-4">
                        <div className="input-group">
                            <DateOfBirthPicker
                                name="date_of_birth"
                                value={dateOfBirth}
                                onChange={(date) => {
                                    // we store the date internally as an ISO string.
                                    setValue("date_of_birth", date, {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                }}
                                onSelect={handleSubmit(onSubmit)}
                            />
                        </div>
                    </div>
                    <div className="col-2">
                        <div className="input-group">
                            <button type="submit" className="btn btn-primary">
                                Zoeken
                            </button>
                        </div>
                    </div>
                </div>
                {Object.entries(errors).map(([field, error]) => (
                    <FormError key={field} fieldName={labels[field]} error={error} />
                ))}
            </form>

            {(results || proCRMResults) && (
                <>
                    <table className="table">
                        <thead>
                            <tr>
                                <th width="135">NIBHV#</th>
                                <th width="250">Naam</th>
                                <th>Geboortedatum</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {results && results.length === 0 ? (
                                <tr>
                                    <td colSpan="4">Staat de juiste cursist er niet tussen? Voeg dan de juiste cursist toe.</td>
                                </tr>
                            ) : (
                                (results || []).map((user) => {
                                    const alreadyOnCourse = currentStudentUUIDS.includes(user.uuid);
                                    return (
                                        <tr key={user.uuid}>
                                            <td className="lighter">{user.nibhv_number}</td>
                                            <td className={classNames({ lighter: alreadyOnCourse })}>{user.full_name}</td>
                                            <td className={classNames({ lighter: alreadyOnCourse })}>
                                                {user.date_of_birth ? moment(user.date_of_birth).format("DD-MM-YYYY") : "-"}
                                            </td>
                                            <td className={classNames("text-right", { lighter: alreadyOnCourse })}>
                                                {alreadyOnCourse ? (
                                                    "Al op de cursus"
                                                ) : (
                                                    <button className="link" onClick={defaultPrevented(handleSelect, user)}>
                                                        Toevoegen aan cursus
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            {proCRMResults && proCRMResults.length > 0 && (
                                <tr>
                                    <td colSpan="4">
                                        <span className="lighter">Resultaten uit het oude portaal.</span>
                                    </td>
                                </tr>
                            )}
                            {proCRMResults &&
                                proCRMResults.map((user) => {
                                    if (user.pro_crm_relation_id === 0) {
                                        return null;
                                    }
                                    return (
                                        <tr key={user.pro_crm_relation_id}>
                                            <td className="lighter">{user.pro_crm_candidate_id}</td>
                                            <td>{[user.first_name || user.initials, user.last_name_prefix, user.last_name].filter((v) => v).join(" ")}</td>
                                            <td>{user.date_of_birth ? moment(user.date_of_birth).format("DD-MM-YYYY") : "-"}</td>
                                            <td className="text-right">
                                                <button className="link" onClick={defaultPrevented(handleImportAndSelect, user.pro_crm_relation_id)}>
                                                    Toevoegen aan cursus
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                    {!loading && (
                        <>
                            <p>
                                Staat de juiste cursist er niet tussen?{" "}
                                <button className="link" onClick={handleCreate}>
                                    Maak een nieuwe cursist aan
                                </button>
                            </p>
                            {previousCourses.length > 0 && (
                                <div>
                                    <b>Eerdere E-learning(s) gevonden, we raden aan de cursist op het oude profiel in te schrijven.</b>
                                    <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                                        {previousCourses.map((course) => (
                                            <li key={course.uuid}>
                                                <button className="link" onClick={() => window.open(`/courses/edit/${course.uuid}/students`)}>
                                                    {course.id} - {course.display_name}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </Dialog>
    );
}

function exactOnlineContactURL(uuid) {
    // fixme: FFS! Let's move this to a .env file!!!
    const eolDivision = APP_CONFIG.environment !== "development" ? "875515" : "1826261";
    return `https://start.exactonline.nl/docs/CRMContactCard.aspx?_ID=%7b${uuid}%7d&_Division_=${eolDivision}`;
}

/**
 * Matches a UUID4 string.
 * @type {RegExp}
 */
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Checks whether the given string is a placeholder email address.
 * The system uses "<uuid4>@nibhv.nl" as placeholder emails.
 * todo: lift up to globally reusable utility functions.
 * @param email
 * @returns {false}
 */
function isPlaceholderEmail(email) {
    const parts = email.toLowerCase().split("@");
    return parts.length === 2 && parts[0].match(uuidPattern) && parts[1] === "nibhv.nl";
}

/**
 * Confirmation dialog for adding an existing student to the course.
 * @param {object} student The student data
 * @param {boolean} imported Whether this user has been imported from ProCRM.
 * @param {boolean} visible Whether the <Dialog /> is visible.
 * @param {object} course The course data.
 * @param {Function} onSubmit Callback to add the student to the course.
 * @param {Function} onRefresh Callback to refresh the given student. This is used to refresh the student data when, for example, a change was made in the back-end.
 * @param {Function} onCancel Callback to cancel adding, return to the previous dialog.
 * @param {Function} onClose Callback to close the dialog.
 * @param insuranceAvailable Whether insurance is available.
 * @param employerSpecificAvailable Whether employer specific is available.
 * @param containsELearning Whether the course contains e-learning.
 */
function AddStudentDialog({
    course,
    student,
    imported,
    visible,
    onSubmit,
    onRefresh,
    onCancel,
    onClose,
    insuranceAvailable,
    employerSpecificAvailable,
    containsELearning,
    endTerms: et,
    competenceProfile: cp
}) {
    const currentUser = useCurrentUser();
    const schema = {
        date_of_birth: yup.string().label("Geboortedatum").nullable().required()
    };
    if (!student?.first_name) {
        // field is optionally added
        Object.assign(schema, { first_name: yup.string().label("Voornaam") });
    }
    if (insuranceAvailable) {
        Object.assign(schema, { insured: yup.boolean().label("Verzekering").required() });
    }
    if (employerSpecificAvailable) {
        Object.assign(schema, { employer_specific: yup.boolean().label("Bedrijfsspecifiek").required() });
    }

    const emailField = yup.string().label("E-mailadres").email().nullable();
    Object.assign(schema, {
        email: containsELearning ? emailField.required("E-mail is verplicht wanneer een cursus E-learning bevat.") : emailField
    });

    const { date_of_birth, ...studentData } = student || {};
    if (studentData.email && isPlaceholderEmail(studentData.email)) {
        // Placeholder e-mail, the correct e-mail should be provided.
        studentData.email = "";
    }
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        setError,
        clearErrors,
        formState: { isDirty, dirtyFields, errors }
    } = useForm({
        defaultValues: {
            insured: false,
            employer_specific: false,
            date_of_birth: date_of_birth && new Date(date_of_birth).toISOString(),
            ...studentData
        },
        resolver: yupResolver(yup.object(schema))
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [endTerms, setEndTerms] = useState([]);
    const [competenceProfile, setCompetenceProfile] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [endTermsToExempt, setEndTermsToExempt] = useState([]);

    useEffect(() => {
        setCompetenceProfile(cp);
        setEndTerms(et);
    }, [et, cp]);

    // When our student changes, we want to reset our form:
    useEffect(() => {
        reset({
            insured: false,
            employer_specific: false,
            date_of_birth: date_of_birth && new Date(date_of_birth).toISOString(),
            ...studentData
        });
    }, [student]);

    if (!student) {
        // our hooks are prepared.
        // no need to render anything.
        return null;
    }

    const resetForm = () =>
        reset(
            {
                date_of_birth: null,
                email: "",
                first_name: "",
                insured: false,
                employer_specific: false
            },
            { keepErrors: false }
        );
    const handleClose = defaultPrevented(() => {
        resetForm();
        onClose();
    });
    const handleCancel = defaultPrevented(onCancel);
    const handleRefresh = defaultPrevented(() => {
        setIsRefreshing(true);
        onRefresh()
            .then(
                () => clearErrors(),
                (error) => {
                    setError("non_field_errors", {
                        message: "Er is een fout opgetreden bij het ophalen van de gegevens."
                    });
                }
            )
            .finally(() => setIsRefreshing(false));
    });

    /**
     * Set form errors
     * @param {Object|Array|string} data
     */
    const setErrorsFromData = (data) => {
        if (Array.isArray(data)) {
            data.forEach(setErrorsFromData);
        } else if (isString(data)) {
            setError("non_field_errors", {
                message: data
            });
        } else if (isObjectLiteral(data)) {
            Object.entries(data).forEach(([field, message]) => {
                setError(["uuid"].includes(field) ? "non_field_errors" : field, {
                    message
                });
            });
        } else {
            console.warn("Unknown error format:", data);
        }
    };

    /**
     * If the form is dirty, attempt to update the user. Then submit the data so the
     * user can be added to a course.
     * @param user
     */
    const updateAndSubmit = (user) => {
        return new Promise((resolve) => {
            if (!isDirty) {
                // form isn't modified.
                return resolve(user);
            }
            // only patch these fields on the user, ignore any other fields.
            const patch = ["first_name", "date_of_birth", "email"].reduce((patch, field) => {
                if (user[field]) {
                    dirtyFields[field] && Object.assign(patch, { [field]: user[field] });
                }
                return patch;
            }, {});

            if (patch.date_of_birth) {
                patch.date_of_birth = moment(patch.date_of_birth).format("YYYY-MM-DD");
            }

            if (Object.keys(patch).length === 0) {
                // nothing to patch
                return resolve(user);
            }

            // note: we always resolve with the form data as that contains additional
            // parameters such as whether to insure or employer specific.
            axios
                .patch(`/v1/users/${user.uuid}/`, patch)
                .then(() => resolve(user))
                .catch((error) => {
                    console.warn("Update failed", error);
                    // console.info(error.response.data);
                    setErrorsFromData("Er is een fout opgetreden bij het updaten van de gegevens.");
                    setErrorsFromData(error.response.data);
                    // bad request? no permissions?

                    // we still resolve as we still want to add this user to the course.
                    // resolve(user);
                });
        }).then((updatedData) => {
            if (onSubmit) {
                onSubmit(updatedData, endTermsToExempt).then(() => {
                    resetForm();
                    setSubmitted(false);
                    onClose && onClose();
                }, setErrorsFromData);
            } else {
                resetForm();
                onClose && onClose();
            }
        });
    };

    const isReadOnly = (field) => {
        return student.read_only_fields && student.read_only_fields.includes(field);
    };

    const canAuthorize = hasPermission(currentUser.permissions, "companyuserauthorization", "create");

    // register date_of_birth here. We can't pass it to <DateOfBirthPicker /> because
    // it doesn't handle the ref register() returns.
    register("date_of_birth");

    const email = watch("email");

    return (
        <Dialog title="Cursist toevoegen aan cursus" onClose={handleClose} visible={visible}>
            <form
                className="mt-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmitted(true);
                    handleSubmit(updateAndSubmit)();
                }}
            >
                {imported && (
                    <div className="alert alert-info">
                        <strong>Let op:</strong> De gegevens van deze cursist worden overgenomen uit het oude ExamenPortaal.
                        <br />
                        De cursist krijgt een nieuw NIBHV-nummer. Wijzig deze gegevens ook in jouw administratie.
                    </div>
                )}
                <FormError error={errors.non_field_errors} />
                {errors.date_of_birth && submitted && <div className="form-error alert alert-danger">Vul a.u.b. een geboortedatum in.</div>}

                <div className="row">
                    <div className="col-3">
                        <strong>NIBHV-nummer</strong>
                    </div>
                    <div className="col-9">{student.nibhv_number}</div>
                </div>
                <div className="row">
                    <div className="col-3">
                        <strong>Naam</strong>
                    </div>
                    <div className="col-9">{student.full_name}</div>
                </div>
                <hr />

                {student.eol_id &&
                    (!canAuthorize ? (
                        // user can't authorize
                        <div className="lighter mb-1">Als je deze persoon wilt wijzigen neem dan contact op met uw NIBHV contactpersoon.</div>
                    ) : (
                        // todo: previous code had a TODO to display the trainer contact(s) of the course trainer.
                        // user is able to authorize
                        <>
                            <div className="lighter mb-1">
                                Deze persoon wordt beheerd in Exact Online. Het kan tot een paar minuten duren voor de info is bijgewerkt na een wijziging in Exact
                                Online.
                            </div>
                            {hasAnyRole(currentUser.roles, "certification", "development-and-advice") ? (
                                [
                                    <a key="eol-3" className="btn btn-secondary hoverable mb-1" href={exactOnlineContactURL(student.eol_id)} target="_blank">
                                        Deze persoon in Exact Online bewerken
                                    </a>,
                                    // todo: so this spinner refreshes the user data?
                                    //
                                    <i key="eol-4" className={classNames("fa", "fa-refresh", "hoverable", { "fa-spin": isRefreshing })} onClick={handleRefresh} />
                                ]
                            ) : (
                                // user does not have an NIBHV role:
                                <div key="eol-2" className="lighter mb-1">
                                    Neem contact op met NIBHV als onderstaande gegevens niet juist zijn.
                                </div>
                            )}
                        </>
                    ))}

                <div className="row mt-2 mb-2">
                    {!student.first_name && (
                        <div className="col-12">
                            <Input
                                {...register("first_name")}
                                type="text"
                                defaultValue={student.first_name}
                                readOnly={isReadOnly("first_name")}
                                label="Roepnaam"
                                errors={errors}
                            />
                        </div>
                    )}
                    <div className="col-12">
                        <label>Geboortedatum *</label>
                        <DateOfBirthPicker
                            name="date_of_birth"
                            onChange={(date) => {
                                // we store the date internally as an ISO string.
                                setValue("date_of_birth", date, {
                                    shouldValidate: true,
                                    shouldDirty: true
                                });
                            }}
                            value={watch("date_of_birth")}
                            readOnly={isReadOnly("date_of_birth")}
                        />
                    </div>
                </div>
                <div className="row mt-2 mb-2">
                    <div className="col-12">
                        <Input
                            {...register("email")}
                            type="email"
                            defaultValue={student.email}
                            readOnly={isReadOnly("email")}
                            label="E-mailadres"
                            errors={errors}
                            required={containsELearning}
                        />
                        <EmailHelpText value={email} containsELearning={containsELearning} />
                    </div>
                </div>

                {(insuranceAvailable || employerSpecificAvailable) && (
                    <>
                        <div className="row align-items-end mt-2 mb-2">
                            {insuranceAvailable && (
                                <div className="col-6">
                                    <Select label="Wil je deze cursist verzekeren op deze cursus?" className="form-control col-3" {...register("insured")}>
                                        <option value={true}>Ja</option>
                                        <option value={false}>Nee</option>
                                    </Select>
                                </div>
                            )}
                            {employerSpecificAvailable && (
                                <div className="col-6">
                                    <Select
                                        label="Wil je voor deze cursist ook bedrijfsspecifiek (BS) certificeren?"
                                        className="form-control col-3"
                                        {...register("employer_specific")}
                                    >
                                        <option value={true}>Ja</option>
                                        <option value={false}>Nee</option>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <span style={{ fontWeight: "bold", display: "block", marginTop: "10px" }}>Vrijstellingen</span>
                        {endTerms.map((endTerm) => {
                            return (
                                <div className="end-term-container" style={{ marginRight: "10px" }} key={endTerm.uuid}>
                                    <span style={{ display: "block" }} data-tip data-for={"end-term-" + endTerm.uuid}>
                                        {endTerm?.abbreviation || endTerm?.name?.[0] || "-"}
                                    </span>
                                    <input
                                        disabled={!endTerm?.can_be_exempted || student.cancelled}
                                        type="checkbox"
                                        name={endTerm.name}
                                        value={endTerm.uuid}
                                        checked={endTermsToExempt.includes(endTerm.uuid)}
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            if (e.target.checked) {
                                                setEndTermsToExempt([...endTermsToExempt, id]);
                                            } else {
                                                setEndTermsToExempt(endTermsToExempt.filter((selectedId) => selectedId !== id));
                                            }
                                        }}
                                    />
                                    <ReactTooltip id={"end-term-" + endTerm.uuid} effect={"solid"} place={"top"} delayShow={250} delayHide={125}>
                                        Vrijstelling voor: {endTerm?.name}
                                    </ReactTooltip>
                                </div>
                            );
                        })}
                    </div>
                    {containsELearning && <ELearningExemptionWarning />}
                </div>
                <hr />
                <div className="row justify-content-end col-12">
                    {onCancel && (
                        <button type="button" className="btn btn-secondary" key="cancel" onClick={handleCancel}>
                            Terug
                        </button>
                    )}
                    <button type="submit" className="btn btn-primary" key="no">
                        Toevoegen
                    </button>
                </div>
            </form>
        </Dialog>
    );
}

/**
 * Dialog to create a new user to add it to the course.
 * @param visible Whether the <Dialog /> is visible.
 * @param onSubmit Callback to add the created user to the course.
 * @param onCancel Callback to cancel adding a new user and return to the previous dialog.
 * @param onClose Callback to close the dialog.
 * @param defaultValues Initial form data. This is passed from the find student dialog.
 * @param insuranceAvailable Whether insurance is available.
 * @param employerSpecificAvailable Whether employer specific is available.
 * @param containsELearning Whether the course contains e-learning.
 * @returns {JSX.Element}
 */
function CreateStudentDialog({
    course,
    visible,
    onSubmit,
    onCancel,
    onClose,
    defaultValues,
    insuranceAvailable,
    employerSpecificAvailable,
    containsELearning,
    endTerms: et,
    competenceProfile: cp
}) {
    const defaults = {
        initials: "",
        first_name: "",
        last_name_prefix: "",
        last_name: "",
        date_of_birth: null,
        email: "",
        insured: false,
        employer_specific: false
    };
    const schema = {
        initials: yup.string().label("Initialen").required(),
        first_name: yup.string().label("Voornaam"),
        last_name_prefix: yup.string().label("Tussenvoegsel"),
        last_name: yup.string().label("Achternaam").required(),
        date_of_birth: yup.string().label("Geboortedatum").nullable().required(),
        email: yup.string().label("E-mailadres").email()
    };
    if (containsELearning) {
        Object.assign(schema, {
            email: schema.email.required()
        });
    }
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: defaults,
        resolver: yupResolver(yup.object(schema))
    });
    const resetForm = () => reset(defaults);
    const handleCancel = defaultPrevented(onCancel);
    const handleClose = defaultPrevented(() => {
        resetForm();
        onClose();
    });
    const [endTerms, setEndTerms] = useState([]);
    const [selectedEndTerms, setSelectedEndTerms] = useState([]);
    const [competenceProfile, setCompetenceProfile] = useState(null);

    useEffect(() => {
        setCompetenceProfile(cp);
        setEndTerms(et);
    }, [et, cp]);

    useEffect(() => {
        if (defaultValues) {
            // we have received new defaultValues (from <FindStudentDialog />)
            reset(Object.assign({}, defaults, defaultValues));
        }
    }, [defaultValues]);

    /**
     * Creates the new user with the form state and triggers onSubmit with the new user.
     * @param {object} user
     * @returns {Promise<any>}
     */
    const createAndSubmit = async (user) => {
        if (user.date_of_birth) {
            Object.assign(user, { date_of_birth: moment(user.date_of_birth).format("YYYY-MM-DD") });
        }

        return await axios.post("/v1/students/?domain=cursist", user).then(
            async (response) => {
                const createdUser = response.data;
                resetForm();
                onSubmit && onSubmit(Object.assign({ uuid: createdUser.uuid }, user), selectedEndTerms.map(e => e.uuid));
            },
            (error) => {
                if (error.response.status !== 400) {
                    setError("non_field_errors", {
                        message: "Er is iets misgegaan bij het aanmaken van de gebruiker."
                    });
                    console.warn(`Unknown error ${error.response.status}`, error, error.response.data);
                    return;
                }
                // 400 responses will return an object with errors for each faulty field.
                const fieldErrors = error.response.data;
                for (const [field, errors] of Object.entries(fieldErrors)) {
                    setError(field, {
                        type: "manual",
                        message: `${Array.isArray(errors) ? errors[0] : errors}`
                    });
                }
            }
        );
    };

    register("date_of_birth", { required: true });

    const email = watch("email");
    const certificateName = formalName(...watch(["initials", "first_name", "last_name_prefix", "last_name"]));

    return (
        <Dialog title="Nieuwe cursist toevoegen" onClose={handleClose} visible={visible}>
            <form onSubmit={handleSubmit(createAndSubmit)}>
                <FormError error={errors.non_field_errors} />
                <div className="row mt-2 mb-2">
                    <div className="col-12">
                        <Input {...register("initials", { required: true })} label="Voorletters" errors={errors} required autoFocus />
                    </div>
                </div>
                <div className="row mt-2 mb-2">
                    <div className="col-12">
                        <Input {...register("first_name", { required: false })} label="Roepnaam" errors={errors} />
                    </div>
                </div>
                <div className="row mt-2 mb-2">
                    <div className="col-4">
                        <Input {...register("last_name_prefix")} label="Tussenvoegsel" errors={errors} />
                    </div>
                    <div className="col-8">
                        <Input {...register("last_name", { required: true })} label="Achternaam" errors={errors} required />
                    </div>
                </div>
                <div className="row">
                    <div className="col mt-3">
                        {certificateName ? (
                            <p className="font-italic">
                                Deze cursist zal als <strong>{certificateName}</strong> worden getoond op de certificering.
                            </p>
                        ) : (
                            <>&nbsp;</>
                        )}
                    </div>
                </div>
                <div className="row mt-2 mb-2">
                    <div className="col-6">
                        <label>Geboortedatum *</label>
                        <DateOfBirthPicker
                            name="date_of_birth"
                            onChange={(date) => {
                                // we store the date internally as an ISO string.
                                setValue("date_of_birth", date, {
                                    shouldValidate: true,
                                    shouldDirty: true
                                });
                            }}
                            value={watch("date_of_birth")}
                        />
                        {errors.date_of_birth && <div className="form-error alert alert-danger">{errors.date_of_birth.message}</div>}
                    </div>
                </div>
                <div className="row mt-2 mb-2">
                    <div className="col-12">
                        <Input {...register("email", { required: containsELearning })} type="email" label="E-mailadres" errors={errors} required={containsELearning} />
                        <EmailHelpText value={email} containsELearning={containsELearning} />
                    </div>
                </div>
                {(insuranceAvailable || employerSpecificAvailable) && (
                    <div className="row align-items-end mt-2 mb-2">
                        {insuranceAvailable && (
                            <div className="col-6">
                                <Select label="Wil je deze cursist verzekeren op deze cursus?" className="form-control col-3" {...register("insured")}>
                                    <option value={true}>Ja</option>
                                    <option value={false}>Nee</option>
                                </Select>
                            </div>
                        )}
                        {employerSpecificAvailable && (
                            <div className="col-6">
                                <Select
                                    label="Wil je voor deze cursist ook bedrijfsspecifiek (BS) certificeren?"
                                    className="form-control col-3"
                                    {...register("employer_specific")}
                                >
                                    <option value={true}>Ja</option>
                                    <option value={false}>Nee</option>
                                </Select>
                            </div>
                        )}
                    </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <span style={{ fontWeight: "bold", display: "block", marginTop: "10px" }}>Vrijstellingen</span>
                        {endTerms.map((endTerm) => {
                            return (
                                <div className="end-term-container" style={{ marginRight: "10px" }} key={endTerm.uuid}>
                                    <span style={{ display: "block" }} data-tip data-for={"end-term-" + endTerm.uuid}>
                                        {endTerm?.abbreviation || endTerm?.name?.[0] || "-"}
                                    </span>
                                    <div className="end-term-container" key={`${endTerm.uuid}-create-student`}>
                                        <input
                                            type="checkbox"
                                            name={endTerm.name}
                                            value={endTerm}
                                            checked={selectedEndTerms.some((e) => e.uuid === endTerm.uuid)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedEndTerms((oldEndTerms) => [...oldEndTerms, endTerm]);
                                                } else {
                                                    setSelectedEndTerms((oldEndTerms) => oldEndTerms.filter((item) => item.uuid !== endTerm.uuid));
                                                }
                                            }}
                                        />
                                    </div>
                                    <ReactTooltip id={"end-term-" + endTerm.uuid} effect={"solid"} place={"top"} delayShow={250} delayHide={125}>
                                        Vrijstelling voor: {endTerm?.name}
                                    </ReactTooltip>
                                </div>
                            );
                        })}
                    </div>
                    {containsELearning && <ELearningExemptionWarning />}
                </div>
                <hr />
                <div className="row justify-content-end col-12">
                    {onCancel && (
                        <button type="button" className="btn btn-secondary" key="cancel" onClick={handleCancel}>
                            Terug
                        </button>
                    )}
                    <button type="submit" className="btn btn-primary" key="no">
                        Aanmaken
                    </button>
                </div>
            </form>
        </Dialog>
    );
}

function ELearningExemptionWarning({ style, ...props }) {
    return (
        <p style={{ color: "red", fontWeight: "700", marginTop: "15px", maxWidth: "440px", ...style }} {...props}>
            LET OP: Als je niet nu direct (of 28 dagen voor de praktijk) aangeeft dat er vrijstelling is voor deze cursist dan melden we de cursist aan voor de volledige
            e-learning. Als nadat de cursist is aangemeld in NIBHV Academie toch een aanpassing gedaan word in de vrijstellingen dan veranderd dat vanaf dan niet meer aan
            de inschrijving op de content uitvoering in NIBHV Academie.
        </p>
    );
}
